import { formatMagnitude } from '@dawn/core/utils';
import { SIZE_UNITS, type SizeUnit } from '@dawn/feature-config/widgets';
import { StaleDuplicateDataBody } from '@dawn/ui';

import type { WidgetConfig } from '@/config/dashboard.types';

import { WidgetShell } from '../WidgetShell';

// Inline color constants per Decision Log #F. Body consumers expect CSS color
// strings (not Tailwind classes) on these props, so we keep raw hex literals at
// module scope and pass them through as-is.
const COLOR_DUPLICATED = '#FF9700' as const;
const COLOR_STALE = '#FFCA27' as const;
const COLOR_REMAINDER = '#FFEACE' as const;
const COLOR_TOTAL_DOT = '#FFEACE' as const;

const VALID_SIZE_UNITS = new Set<string>(SIZE_UNITS);

interface ResponseSummary {
  id: 'total' | 'duplicated' | 'stale';
  label: string;
  sizeValue: number;
  sizeUnit: SizeUnit;
  count: number;
  sizePercent: number;
  countPercent: number;
  isHighlighted?: boolean;
}

interface ResponseStale {
  count: number;
  sizeValue: number;
  sizeUnit: SizeUnit;
  countPercent: number;
  sizePercent: number;
  covered: boolean;
  accessHistoryStart: string | null;
  asOf: string;
}

interface ResponseData {
  total: ResponseSummary;
  duplicated: ResponseSummary;
  /** The top-level `stale` object, never a `summaries` entry. See the narrow. */
  stale?: ResponseStale;
}

/** The caller has already matched `id`; this checks everything else on the row. */
function narrowSummary(value: unknown): ResponseSummary | null {
  const s = value as Record<string, unknown>;
  if (typeof s.label !== 'string') return null;
  if (typeof s.sizeValue !== 'number' || !Number.isFinite(s.sizeValue) || s.sizeValue < 0) {
    return null;
  }
  if (typeof s.sizeUnit !== 'string' || !VALID_SIZE_UNITS.has(s.sizeUnit)) return null;
  if (typeof s.count !== 'number' || !Number.isFinite(s.count) || s.count < 0) return null;
  if (
    typeof s.sizePercent !== 'number' ||
    !Number.isFinite(s.sizePercent) ||
    s.sizePercent < 0 ||
    s.sizePercent > 100
  ) {
    return null;
  }
  if (
    typeof s.countPercent !== 'number' ||
    !Number.isFinite(s.countPercent) ||
    s.countPercent < 0 ||
    s.countPercent > 100
  ) {
    return null;
  }
  if (s.isHighlighted !== undefined && typeof s.isHighlighted !== 'boolean') return null;
  return s as unknown as ResponseSummary;
}

/** A malformed stale object drops the stale half rather than erroring the card. */
function narrowStale(value: unknown): ResponseStale | null {
  if (value === null || typeof value !== 'object') return null;
  const s = value as Record<string, unknown>;
  if (typeof s.count !== 'number' || !Number.isFinite(s.count) || s.count < 0) return null;
  if (typeof s.sizeValue !== 'number' || !Number.isFinite(s.sizeValue) || s.sizeValue < 0) {
    return null;
  }
  if (typeof s.sizeUnit !== 'string' || !VALID_SIZE_UNITS.has(s.sizeUnit)) return null;
  for (const field of ['countPercent', 'sizePercent'] as const) {
    const percent = s[field];
    if (typeof percent !== 'number' || !Number.isFinite(percent) || percent < 0 || percent > 100) {
      return null;
    }
  }
  if (typeof s.covered !== 'boolean') return null;
  if (s.accessHistoryStart !== null && typeof s.accessHistoryStart !== 'string') return null;
  if (typeof s.asOf !== 'string') return null;
  return s as unknown as ResponseStale;
}

/**
 * Hard-reject narrow for the Stale & Duplicate Data API envelope, returning
 * the summaries already resolved by id.
 *
 * Returns `null` on any shape violation so `WidgetShell.renderData` falls
 * through to `WidgetErrorState`. Branches:
 *   - `summaries` not an array, or shorter than the two required entries
 *   - `total` or `duplicated` absent, or either id appearing more than once
 *   - any resolved summary fails `narrowSummary` (label/sizeValue/sizeUnit/count/sizePercent/countPercent/isHighlighted)
 *
 * A `stale` summary inside `summaries` is ignored, never rejected: rejecting
 * would turn the old endpoint's response into an error card instead of a
 * correct duplicate-only one.
 */
export function narrowStaleDuplicateData(input: unknown): ResponseData | null {
  if (input === null || typeof input !== 'object') return null;
  const candidate = input as Record<string, unknown>;
  const summaries = candidate.summaries;
  if (!Array.isArray(summaries) || summaries.length < 2) return null;

  // Unrecognised ids are ignored rather than rejected, so a bundle deployed
  // ahead of an API that adds a summary keeps rendering. A recognised id that
  // appears twice is ambiguous (which entry is the total?) and rejected.
  //
  // `stale` stays out permanently: the pre-TDEV-1037 endpoint serves a fabricated
  // one here, and only a top-level `stale` object tells a real rollup apart.
  const byId = new Map<ResponseSummary['id'], unknown>();
  for (const entry of summaries as unknown[]) {
    if (entry === null || typeof entry !== 'object') continue;
    const id = (entry as Record<string, unknown>).id;
    if (id !== 'total' && id !== 'duplicated') continue;
    if (byId.has(id)) return null;
    byId.set(id, entry);
  }

  const rawTotal = byId.get('total');
  const rawDuplicated = byId.get('duplicated');
  if (rawTotal === undefined || rawDuplicated === undefined) return null;
  const total = narrowSummary(rawTotal);
  const duplicated = narrowSummary(rawDuplicated);
  if (!total || !duplicated) return null;

  const stale = candidate.stale === undefined ? null : narrowStale(candidate.stale);

  return { total, duplicated, ...(stale ? { stale } : {}) };
}

export function StaleDuplicateDataWidget({ config }: { config: WidgetConfig }) {
  return (
    <WidgetShell
      config={config}
      renderData={(envelopeData) => {
        const data = narrowStaleDuplicateData(envelopeData);
        if (!data) return null;

        return (
          <StaleDuplicateDataBody
            duplicatedPercent={data.duplicated.sizePercent}
            total={{
              sizeLabel: `${data.total.sizeValue} ${data.total.sizeUnit}`,
              countLabel: formatMagnitude(data.total.count),
            }}
            duplicated={{
              sizeLabel: `${data.duplicated.sizeValue} ${data.duplicated.sizeUnit}`,
              countLabel: formatMagnitude(data.duplicated.count),
            }}
            stale={
              data.stale
                ? {
                    // Under-covered means the warehouse holds less history than
                    // the window, so there is no measurement to chart or print.
                    // The bar is by file count: stale covers every live file,
                    // while a size exists only for the downloaded subset.
                    percent: data.stale.covered ? data.stale.countPercent : null,
                    sizeLabel: data.stale.covered
                      ? `${data.stale.sizeValue} ${data.stale.sizeUnit}`
                      : 'Collecting',
                    countLabel: data.stale.covered ? formatMagnitude(data.stale.count) : '',
                    color: COLOR_STALE,
                    legendLabel: 'Stale Data',
                    descriptorLabel: 'Stale Data',
                    barAriaLabel: 'Stale data share of all files',
                  }
                : undefined
            }
            duplicatedColor={COLOR_DUPLICATED}
            remainderColor={COLOR_REMAINDER}
            totalDotColor={COLOR_TOTAL_DOT}
            duplicatedLegendLabel="Duplicated Data"
            totalDescriptorLabel="Analysed Data"
            duplicatedDescriptorLabel="Duplicated Data"
          />
        );
      }}
    />
  );
}
